import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, LabelList, Legend
} from 'recharts';
import {
  Grid, Box, Paper, Typography,
  Button, useTheme, alpha, TextField, Chip, Avatar, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  FormControl, Select, MenuItem, InputLabel
} from '@mui/material';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import {
  BarChart as BarChartIcon, People, Inventory,
  AddCircle, PersonAdd, AddShoppingCart, TrendingUp, Receipt,
  AccountBalance, ShoppingCart, ArrowUpward, Assignment
} from '@mui/icons-material';
import { getDashboardData, setPeriod, setDateRange, setDocumentType } from "../../../app/store/slices/dashboardSlice";
import { useStoreInfo } from '../../../hooks/useCompanyInfo';
import AddCustomerDialog from '../../customers/components/AddCustomerDialog';
import AddProductDialog from '../../product/components/AddProductDialog';
import { toast } from 'react-toastify';

const STATUS_COLORS = ['#43a047', '#fbc02d', '#d32f2f'];
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const period = useSelector(state => state.dashboard.period);
  const dashboardData = useSelector(state => state.dashboard.data);
  const dateRange = useSelector(state => state.dashboard.dateRange);
  const documentType = useSelector(state => state.dashboard.documentType || 'all');
  const { storeInfo } = useStoreInfo();
  
  const [customStartDate, setCustomStartDate] = useState(dateRange?.startDate || '');
  const [customEndDate, setCustomEndDate] = useState(dateRange?.endDate || '');
  const [currentQuarter, setCurrentQuarter] = useState(1);
  
  // Dialog states
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);

  useEffect(() => {
    // Calculate current quarter
    const now = new Date();
    const month = now.getMonth() + 1;
    const quarter = Math.ceil(month / 3);
    setCurrentQuarter(quarter);
    
    dispatch(getDashboardData({ dateRange, documentType }));
  }, [dispatch, dateRange, documentType]);

  useEffect(() => {
    // Debug: Log dashboard data when it changes
    if (dashboardData) {
    }
  }, [dashboardData]);

  const getQuarterDates = (quarter, year = new Date().getFullYear()) => {
    const quarterStartMonth = (quarter - 1) * 3;
    const startDate = new Date(year, quarterStartMonth, 1);
    const endDate = new Date(year, quarterStartMonth + 3, 0);
    return {
      startDate: formatLocalDate(startDate),
      endDate: formatLocalDate(endDate)
    };
  };

  const handlePeriodChange = (e) => {
    const selectedPeriod = e.target.value;
    dispatch(setPeriod(selectedPeriod));
    
    const now = new Date();
    const year = now.getFullYear();
    
    let newDateRange = null;
    
    switch(selectedPeriod) {
      case 'today':
        const today = formatLocalDate(now);
        newDateRange = { startDate: today, endDate: today };
        break;
      case 'weekly':
        // Show current month from start to today, broken into weeks
        const weeklyMonthStart = new Date(year, now.getMonth(), 1);
        newDateRange = {
          startDate: formatLocalDate(weeklyMonthStart),
          endDate: formatLocalDate(now)
        };
        break;
      case 'lastmonth':
        const lastMonthStart = new Date(year, now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(year, now.getMonth(), 0);
        newDateRange = {
          startDate: formatLocalDate(lastMonthStart),
          endDate: formatLocalDate(lastMonthEnd)
        };
        break;
      case 'monthly':
        const monthStart = new Date(year, now.getMonth(), 1);
        const monthEnd = new Date(year, now.getMonth() + 1, 0);
        newDateRange = {
          startDate: formatLocalDate(monthStart),
          endDate: formatLocalDate(monthEnd)
        };
        break;
      case 'monthtodate':
        const mtdStart = new Date(year, now.getMonth(), 1);
        newDateRange = {
          startDate: formatLocalDate(mtdStart),
          endDate: formatLocalDate(now)
        };
        break;
      case 'q1':
      case 'q2':
      case 'q3':
      case 'q4':
        const quarterNum = parseInt(selectedPeriod.substring(1));
        newDateRange = getQuarterDates(quarterNum, year);
        break;
      case 'ytd':
        const yearStart = new Date(year, 0, 1);
        newDateRange = {
          startDate: formatLocalDate(yearStart),
          endDate: formatLocalDate(now)
        };
        break;
      case 'lastyear':
        const lastYearStart = new Date(year - 1, 0, 1);
        const lastYearEnd = new Date(year - 1, 11, 31);
        newDateRange = {
          startDate: formatLocalDate(lastYearStart),
          endDate: formatLocalDate(lastYearEnd)
        };
        break;
      case 'custom':
        // For custom, just set period and let user enter dates in the inline fields
        // Don't set dateRange yet - wait for user to enter dates
        return;
      default:
        newDateRange = null;
    }
    
    if (newDateRange) {
      setCustomStartDate(newDateRange.startDate);
      setCustomEndDate(newDateRange.endDate);
    }

    dispatch(setDateRange(newDateRange));
  };

  const handleCustomDateApply = () => {
    if (customStartDate && customEndDate) {
      dispatch(setDateRange({ startDate: customStartDate, endDate: customEndDate }));
    }
  };

  const handleQuickPeriod = (value) => handlePeriodChange({ target: { value } });

  const DOC_TYPES = [
    { value: 'all',        label: t('all_documents') || 'All' },
    { value: 'Invoice',    label: t('invoice')       || 'Invoice' },
    { value: 'Receipt',    label: t('receipt')       || 'Receipt' },
  ];

  const handleDocTypeChange = (value) => {
    dispatch(setDocumentType(value));
  };
  
  // Dialog handlers
  const handleCustomerCreated = (customer) => {
    toast.success(t("customer_created_successfully") || "Customer created successfully!");
    // Optionally navigate to customer page or refresh dashboard data
  };
  
  const handleProductCreated = (product) => {
    toast.success(t("product_created_successfully") || "Product created successfully!");
    // Optionally navigate to product page or refresh dashboard data
  };

  const salesData = useMemo(() => {
    if (!dashboardData || !dashboardData.TotalSales || dashboardData.TotalSales.length === 0) {
      return MONTHS.map(month => ({
        month,
        sales: 0,
        salesExclVAT: 0,
        vat: 0,
        salesInclVAT: 0,
      }));
    }
    
    if (period === "monthly") {
      // Show all 12 months on X-axis, map Period from API to month names
      const salesMap = {};
      dashboardData.TotalSales.forEach(item => {
        // Period contains full month name like "January", "February"
        const periodName = item.Period;
        // Map it to short month name (Jan, Feb, etc.)
        const monthIndex = new Date(Date.parse(periodName + " 1, 2026")).getMonth();
        const shortMonth = MONTHS[monthIndex];
        salesMap[shortMonth] = {
          sales: item.Sales || 0,
          salesExclVAT: item.SalesExclVAT || 0,
          vat: item.VAT || 0,
          salesInclVAT: item.SalesInclVAT || 0,
        };
      });
      return MONTHS.map(month => ({
        month,
        sales: salesMap[month]?.sales || 0,
        salesExclVAT: salesMap[month]?.salesExclVAT || 0,
        vat: salesMap[month]?.vat || 0,
        salesInclVAT: salesMap[month]?.salesInclVAT || 0,
      }));
    }
    
    if (period === "weekly") {
      // Show all weeks (W1-W5) on X-axis, Period should contain week info
      const now = new Date();
      const totalDaysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
      const totalWeeks = Math.ceil(totalDaysInMonth / 7);
      const WEEKS = Array.from({ length: totalWeeks }, (_, i) => `W${i + 1}`);
      
      const salesMap = {};
      dashboardData.TotalSales.forEach(item => {
        const weekKey = item.Period || item.Week;
        salesMap[weekKey] = {
          sales: item.Sales || 0,
          salesExclVAT: item.SalesExclVAT || 0,
          vat: item.VAT || 0,
          salesInclVAT: item.SalesInclVAT || 0,
        };
      });
      
      return WEEKS.map(week => ({
        week,
        sales: salesMap[week]?.sales || 0,
        salesExclVAT: salesMap[week]?.salesExclVAT || 0,
        vat: salesMap[week]?.vat || 0,
        salesInclVAT: salesMap[week]?.salesInclVAT || 0,
      }));
    }
    
    // For other periods (today, monthtodate, quarters, ytd, lastyear, custom)
    return dashboardData.TotalSales.map(item => ({
      date: item.Period || item.Date || item.Day,
      month: item.Period,
      week: item.Period,
      quarter: item.Period,
      sales: item.Sales || 0,
      salesExclVAT: item.SalesExclVAT || 0,
      vat: item.VAT || 0,
      salesInclVAT: item.SalesInclVAT || 0,
    }));
  }, [dashboardData, period]);

  const salesTotals = useMemo(() => {
    return salesData.reduce(
      (acc, item) => ({
        salesExclVAT: acc.salesExclVAT + (item.salesExclVAT || 0),
        vat: acc.vat + (item.vat || 0),
        salesInclVAT: acc.salesInclVAT + (item.salesInclVAT || 0),
      }),
      { salesExclVAT: 0, vat: 0, salesInclVAT: 0 }
    );
  }, [salesData]);

  const invoiceStatusData = useMemo(() => {
    if (!dashboardData || !dashboardData.InvoicesStatus || dashboardData.InvoicesStatus.length === 0) {
      return [{ name: "No Data", value: 1 }];
    }
    return dashboardData.InvoicesStatus.map(item => ({
      name: item.Name,
      value: item.Value,
    }));
  }, [dashboardData]);

  const topCustomersData = useMemo(() => {
    if (!dashboardData || !dashboardData.TopCustomers || dashboardData.TopCustomers.length === 0) {
      return [{ name: "", amount: 0 }];
    }
    return dashboardData.TopCustomers.map(item => ({
      name: item.Name,
      amount: item.Amount,
    }));
  }, [dashboardData]);

  const topSellingProductsData = useMemo(() => {
    if (!dashboardData || !dashboardData.TopSellingProducts || dashboardData.TopSellingProducts.length === 0) {
      return [{ name: "", sold: 0 }];
    }
    return dashboardData.TopSellingProducts.map(item => ({
      name: item.Name,
      sold: item.Sold,
    }));
  }, [dashboardData]);

  const getXAxisKey = () => {
    if (salesData.length === 0) return "month";
    
    // Determine X-axis based on period selection
    switch(period) {
      case 'today':
      case 'monthtodate':
      case 'custom':
        return 'date'; // Show dates on X-axis for month-to-date
      case 'weekly':
        return 'week'; // Show weeks on X-axis
      case 'monthly':
        return 'month'; // Show months on X-axis for monthly
      case 'q1':
      case 'q2':
      case 'q3':
      case 'q4':
        return 'quarter'; // Show quarters on X-axis for quarterly
      case 'ytd':
      case 'lastyear':
        return 'month'; // Show months on X-axis for yearly
      default:
        const keys = Object.keys(salesData[0]);
        return keys.find(k => k !== "sales") || "month";
    }
  };

  const getBarSize = (data) => (data.length === 1 ? 32 : 18);

  const renderSalesTotalLabel = ({ x, y, width, height, payload }) => {
    const value = payload?.salesInclVAT;
    if (!value) return null;
    return (
      <text
        x={x + width + 18}
        y={y + height / 2}
        fill={theme.palette.success.main}
        fontWeight={600}
        fontSize={13}
        dominantBaseline="middle"
      >
        €{value}
      </text>
    );
  };

  const renderSalesTotalLabelEnd = ({ x, y, width, height, payload, value: barValue }) => {
    const total = payload?.salesInclVAT;
    if (!total || total === 0) return null;
    
    // Calculate the actual end position of the stacked bar
    const salesExclVAT = payload?.salesExclVAT || 0;
    const vat = payload?.vat || 0;
    const stackedWidth = (salesExclVAT + vat) / total * width;
    const textX = x + stackedWidth + 5;
    
    return (
      <g>
        <rect
          x={textX - 2}
          y={y + height / 2 - 10}
          width={50}
          height={20}
          fill="#fff"
          stroke={theme.palette.success.main}
          strokeWidth={2}
          rx={4}
        />
        <text
          x={textX + 23}
          y={y + height / 2}
          fill={theme.palette.success.main}
          fontWeight={700}
          fontSize={12}
          textAnchor="middle"
          dominantBaseline="middle"
        >
          €{total}
        </text>
      </g>
    );
  };

  const totalSalesAmount = useMemo(() => {
    // Total Sales excluding VAT
    return dashboardData?.TotalSalesAmount || 0;
  }, [dashboardData]);

  const totalSalesAmountInclVAT = useMemo(() => {
    // Total Sales including VAT
    return dashboardData?.TotalSalesAmountInclVAT || 0;
  }, [dashboardData]);

  const totalInvoices = useMemo(() => {
    return invoiceStatusData.reduce((sum, item) => sum + (item.value || 0), 0);
  }, [invoiceStatusData]);

  const totalVAT = useMemo(() => {
    return dashboardData?.TotalVAT || 0;
  }, [dashboardData]);

  const periodLabel = {
    today: t("today"),
    weekly: t("weekly"),
    monthly: t("monthly"),
    monthtodate: t("month_to_date") || "Month to Date",
    q1: t("q1") || "Q1",
    q2: t("q2") || "Q2",
    q3: t("q3") || "Q3",
    q4: t("q4") || "Q4",
    ytd: t("year_to_date") || "Year to Date",
    lastyear: t("last_year") || "Last Year",
    custom: t("custom_date") || "Custom Date",
    annually: t("annually"),
  };

  const quickFilterOptions = [
    { value: 'today', label: t('today') || 'Today' },
    { value: 'monthly', label: t('current_month') || 'Current Month' },
    ...(currentQuarter >= 1 ? [{ value: 'q1', label: 'Q1' }] : []),
    ...(currentQuarter >= 2 ? [{ value: 'q2', label: 'Q2' }] : []),
    ...(currentQuarter >= 3 ? [{ value: 'q3', label: 'Q3' }] : []),
    ...(currentQuarter >= 4 ? [{ value: 'q4', label: 'Q4' }] : []),
    { value: 'ytd', label: t('year_to_date') || 'Year to Date' },
    { value: 'lastyear', label: t('last_year') || 'Last Year' },
  ];

  const documentLabel = documentType === 'all'
    ? t('num_documents') || 'Documents'
    : documentType === 'Receipt'
      ? t('receipts') || 'Receipts'
      : t('invoices') || 'Invoices';
  const documentStatusLabel = documentType === 'all'
    ? t('documents_status') || 'Documents Status'
    : documentType === 'Receipt'
      ? t('receipts_status') || 'Receipts Status'
      : t('invoicesStatus') || 'Invoices Status';

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, mx: 'auto', bgcolor: '#eef2f6', width: '100%', minWidth: 0, maxWidth: 1400, boxSizing: 'border-box', overflowX: 'hidden' }}>
      {/* Header Section */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: { xs: 'flex-start', md: 'center' }, 
        justifyContent: 'space-between', 
        flexDirection: { xs: 'column', md: 'row' },
        mb: 2,
        gap: 1.5,
        px: 1,
      }}>
        <Box>
          <Typography variant="h6" fontWeight="700" color="text.primary">
            {t("dashboard")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {storeInfo?.name || t("welcome_dashboard")}
          </Typography>
        </Box>
        
        {/* Quick filter and document type controls */}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', maxWidth: { xs: '100%', md: 700 } }}>
            <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mr: 0.25 }}>
              {t('quick_filter') || 'Quick Filter'}:
            </Typography>
            {quickFilterOptions.map((option) => (
              <Chip
                key={option.value}
                label={option.label}
                clickable
                color={period === option.value ? 'primary' : 'default'}
                variant={period === option.value ? 'filled' : 'outlined'}
                onClick={() => handleQuickPeriod(option.value)}
                sx={{ fontWeight: period === option.value ? 600 : 400, bgcolor: period === option.value ? undefined : 'white' }}
              />
            ))}
          </Box>

          <TextField
            label={t('start_date') || 'From'}
            type="date"
            value={customStartDate}
            onChange={(e) => setCustomStartDate(e.target.value)}
            onBlur={handleCustomDateApply}
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 160, '& .MuiOutlinedInput-root': { height: 40, borderRadius: 2, bgcolor: 'white', fontSize: '0.9rem' }, '& .MuiInputLabel-root': { fontSize: '0.85rem' } }}
          />
          <TextField
            label={t('end_date') || 'To'}
            type="date"
            value={customEndDate}
            onChange={(e) => setCustomEndDate(e.target.value)}
            onBlur={handleCustomDateApply}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: customStartDate }}
            sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 160, '& .MuiOutlinedInput-root': { height: 40, borderRadius: 2, bgcolor: 'white', fontSize: '0.9rem' }, '& .MuiInputLabel-root': { fontSize: '0.85rem' } }}
          />

          {/* Document type dropdown */}
          <FormControl sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 180, height: 40, bgcolor: 'white', borderRadius: 2 }}>
            <InputLabel>{t('document_type') || 'Document Type'}</InputLabel>
            <Select
              value={documentType}
              label={t('document_type') || 'Document Type'}
              onChange={(e) => handleDocTypeChange(e.target.value)}
              sx={{ height: 40, borderRadius: 2, fontSize: '0.9rem' }}
            >
              {DOC_TYPES.map(dt => (
                <MenuItem key={dt.value} value={dt.value}>{dt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Quick Actions Row */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: { xs: 2.5, md: 1.5 } }}>
        <Button variant="contained" startIcon={<AddCircle sx={{ fontSize: 20 }} />}
          onClick={() => navigate('/invoices/add')}
          sx={{ width: { xs: '100%', sm: 'auto' }, justifyContent: 'flex-start', borderRadius: 2.5, textTransform: 'none', fontWeight: 600, fontSize: { xs: '0.9rem', md: '0.8rem' }, py: { xs: 1, md: 0.75 }, px: { xs: 2.5, md: 2 }, background: `linear-gradient(135deg, #1976d2, #1565c0)`, boxShadow: '0 3px 12px rgba(25,118,210,0.35)', '&:hover': { boxShadow: '0 4px 16px rgba(25,118,210,0.45)' } }}>
          {t("add_invoice")}
        </Button>
        <Button variant="contained" startIcon={<Assignment sx={{ fontSize: 20 }} />}
          onClick={() => navigate('/sales/sale-orders/add')}
          sx={{ width: { xs: '100%', sm: 'auto' }, justifyContent: 'flex-start', borderRadius: 2.5, textTransform: 'none', fontWeight: 600, fontSize: { xs: '0.9rem', md: '0.8rem' }, py: { xs: 1, md: 0.75 }, px: { xs: 2.5, md: 2 }, bgcolor: '#7e57c2', '&:hover': { bgcolor: '#673ab7', boxShadow: '0 4px 16px rgba(126,87,194,0.45)' }, boxShadow: '0 3px 12px rgba(126,87,194,0.35)' }}>
          {t("add_sale_order")}
        </Button>
        <Button variant="contained" startIcon={<Receipt sx={{ fontSize: 20 }} />}
          onClick={() => navigate('/pos')}
          sx={{ width: { xs: '100%', sm: 'auto' }, justifyContent: 'flex-start', borderRadius: 2.5, textTransform: 'none', fontWeight: 600, fontSize: { xs: '0.9rem', md: '0.8rem' }, py: { xs: 1, md: 0.75 }, px: { xs: 2.5, md: 2 }, bgcolor: '#5c6bc0', '&:hover': { bgcolor: '#3f51b5', boxShadow: '0 4px 16px rgba(92,107,192,0.45)' }, boxShadow: '0 3px 12px rgba(92,107,192,0.35)' }}>
          {t("ticket_de_caisse")}
        </Button>
        <Button variant="contained" startIcon={<PersonAdd sx={{ fontSize: 20 }} />}
          onClick={() => setCustomerDialogOpen(true)}
          sx={{ width: { xs: '100%', sm: 'auto' }, justifyContent: 'flex-start', borderRadius: 2.5, textTransform: 'none', fontWeight: 600, fontSize: { xs: '0.9rem', md: '0.8rem' }, py: { xs: 1, md: 0.75 }, px: { xs: 2.5, md: 2 }, bgcolor: '#26a69a', '&:hover': { bgcolor: '#00897b', boxShadow: '0 4px 16px rgba(38,166,154,0.45)' }, boxShadow: '0 3px 12px rgba(38,166,154,0.35)' }}>
          {t("add_customer")}
        </Button>
        <Button variant="contained" startIcon={<AddShoppingCart sx={{ fontSize: 20 }} />}
          onClick={() => setProductDialogOpen(true)}
          sx={{ width: { xs: '100%', sm: 'auto' }, justifyContent: 'flex-start', borderRadius: 2.5, textTransform: 'none', fontWeight: 600, fontSize: { xs: '0.9rem', md: '0.8rem' }, py: { xs: 1, md: 0.75 }, px: { xs: 2.5, md: 2 }, bgcolor: '#ef5350', '&:hover': { bgcolor: '#e53935', boxShadow: '0 4px 16px rgba(239,83,80,0.45)' }, boxShadow: '0 3px 12px rgba(239,83,80,0.35)' }}>
          {t("add_product")}
        </Button>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: { xs: 10, md: 8 }, rowGap: 1 }}>
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', minWidth: 0, mb: { xs: 1, sm: 0 } }}>
          <Paper elevation={0} sx={{ 
            width: '100%', minWidth: 0, minHeight: 96, height: { xs: 'auto', sm: '100%' }, p: { xs: 2, sm: 2, md: 1.5 }, pr: { xs: 8, sm: 8, md: 6.5 }, borderRadius: 3, borderLeft: '4px solid', borderLeftColor: theme.palette.primary.main,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
            position: 'relative', overflow: 'hidden',
            boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s ease',
            '&:hover': { boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.18)}`, transform: 'translateY(-2px)' }
          }}>
            <Box sx={{ position: 'absolute', top: 8, right: 8, width: 48, height: 48, borderRadius: '50%', bgcolor: alpha(theme.palette.primary.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <TrendingUp sx={{ fontSize: 24, color: theme.palette.primary.main }} />
            </Box>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.8, mb: 1, overflowWrap: 'anywhere' }}>
              {t("total_sale_excl_vat") || "SALES (EXCL VAT)"}
            </Typography>
            <Typography fontWeight="800" sx={{ color: theme.palette.primary.dark, fontSize: { xs: '1.25rem', sm: '1.35rem', md: '1.1rem' }, lineHeight: 1.2, overflowWrap: 'anywhere' }}>
              €{totalSalesAmount.toFixed(2)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', minWidth: 0, mb: { xs: 1, sm: 0 } }}>
          <Paper elevation={0} sx={{ 
            width: '100%', minWidth: 0, minHeight: 96, height: { xs: 'auto', sm: '100%' }, p: { xs: 2, sm: 2, md: 1.5 }, pr: { xs: 8, sm: 8, md: 6.5 }, borderRadius: 3, borderLeft: '4px solid', borderLeftColor: theme.palette.warning.main,
            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.04)} 0%, ${alpha(theme.palette.warning.main, 0.1)} 100%)`,
            position: 'relative', overflow: 'hidden',
            boxShadow: `0 2px 12px ${alpha(theme.palette.warning.main, 0.12)}`,
            transition: 'all 0.3s ease',
            '&:hover': { boxShadow: `0 4px 20px ${alpha(theme.palette.warning.main, 0.18)}`, transform: 'translateY(-2px)' }
          }}>
            <Box sx={{ position: 'absolute', top: 8, right: 8, width: 48, height: 48, borderRadius: '50%', bgcolor: alpha(theme.palette.warning.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AccountBalance sx={{ fontSize: 24, color: theme.palette.warning.main }} />
            </Box>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.8, mb: 1, overflowWrap: 'anywhere' }}>
              {t("total_vat") || "TOTAL VAT"}
            </Typography>
            <Typography fontWeight="800" sx={{ color: theme.palette.warning.dark, fontSize: { xs: '1.25rem', sm: '1.35rem', md: '1.1rem' }, lineHeight: 1.2, overflowWrap: 'anywhere' }}>
              €{totalVAT.toFixed(2)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', minWidth: 0, mb: { xs: 1, sm: 0 } }}>
          <Paper elevation={0} sx={{ 
            width: '100%', minWidth: 0, minHeight: 96, height: { xs: 'auto', sm: '100%' }, p: { xs: 2, sm: 2, md: 1.5 }, pr: { xs: 8, sm: 8, md: 6.5 }, borderRadius: 3, borderLeft: '4px solid', borderLeftColor: theme.palette.success.main,
            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.04)} 0%, ${alpha(theme.palette.success.main, 0.1)} 100%)`,
            position: 'relative', overflow: 'hidden',
            boxShadow: `0 2px 12px ${alpha(theme.palette.success.main, 0.12)}`,
            transition: 'all 0.3s ease',
            '&:hover': { boxShadow: `0 4px 20px ${alpha(theme.palette.success.main, 0.18)}`, transform: 'translateY(-2px)' }
          }}>
            <Box sx={{ position: 'absolute', top: 8, right: 8, width: 48, height: 48, borderRadius: '50%', bgcolor: alpha(theme.palette.success.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Receipt sx={{ fontSize: 24, color: theme.palette.success.main }} />
            </Box>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.8, mb: 1, overflowWrap: 'anywhere' }}>
              {t("total_sale_incl_vat") || "SALES (INCL VAT)"}
            </Typography>
            <Typography fontWeight="800" sx={{ color: theme.palette.success.dark, fontSize: { xs: '1.25rem', sm: '1.35rem', md: '1.1rem' }, lineHeight: 1.2, overflowWrap: 'anywhere' }}>
              €{totalSalesAmountInclVAT.toFixed(2)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', minWidth: 0, mb: { xs: 1, sm: 0 } }}>
          <Paper elevation={0} sx={{ 
            width: '100%', minWidth: 0, minHeight: 96, height: { xs: 'auto', sm: '100%' }, p: { xs: 2, sm: 2, md: 1.5 }, pr: { xs: 8, sm: 8, md: 6.5 }, borderRadius: 3, borderLeft: '4px solid', borderLeftColor: theme.palette.info.main,
            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.04)} 0%, ${alpha(theme.palette.info.main, 0.1)} 100%)`,
            position: 'relative', overflow: 'hidden',
            boxShadow: `0 2px 12px ${alpha(theme.palette.info.main, 0.12)}`,
            transition: 'all 0.3s ease',
            '&:hover': { boxShadow: `0 4px 20px ${alpha(theme.palette.info.main, 0.18)}`, transform: 'translateY(-2px)' }
          }}>
            <Box sx={{ position: 'absolute', top: 8, right: 8, width: 48, height: 48, borderRadius: '50%', bgcolor: alpha(theme.palette.info.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ShoppingCart sx={{ fontSize: 24, color: theme.palette.info.main }} />
            </Box>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.8, mb: 1, overflowWrap: 'anywhere' }}>
              {documentLabel}
            </Typography>
            <Typography fontWeight="800" sx={{ color: theme.palette.info.dark, fontSize: { xs: '1.25rem', sm: '1.35rem', md: '1.1rem' }, lineHeight: 1.2, overflowWrap: 'anywhere' }}>
              {totalInvoices}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Row 1: Charts */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Sales Chart */}
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={0} 
            sx={{ 
              borderRadius: 3, 
              height: '100%',
              border: '1px solid', 
              borderColor: alpha(theme.palette.primary.main, 0.2), 
              background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, white 50%, ${alpha(theme.palette.primary.light, 0.03)} 100%)`,
              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
              transition: 'all 0.3s ease',
              '&:hover': { 
                boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.15)}`,
                transform: 'translateY(-4px)'
              }
            }}
          >
            <Box sx={{ 
              px: { xs: 2, sm: 3 }, 
              py: { xs: 2.5, md: 1.25 }, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              flexWrap: 'wrap',
              gap: 1,
              borderBottom: '2px solid', 
              borderColor: alpha(theme.palette.primary.main, 0.1), 
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.light, 0.04)} 100%)`,
              borderRadius: '12px 12px 0 0'
            }}>
              <Typography variant="h6" fontWeight="700" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUp sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                {t("totalSales")}
              </Typography>
              <Chip 
                label={periodLabel[period]} 
                size="small" 
                sx={{ 
                  fontWeight: 600, 
                  bgcolor: alpha(theme.palette.primary.main, 0.15),
                  color: theme.palette.primary.dark,
                  fontSize: '0.75rem'
                }} 
              />
            </Box>
            <Box sx={{ p: { xs: 1.5, sm: 2.5, md: 1.25 }, height: { xs: 380, sm: 340, md: 300 } }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={salesData}
                  layout="vertical"
                  barCategoryGap={salesData.length === 1 ? "20%" : "12%"}
                  margin={{ top: 10, right: 70, left: 5, bottom: 10 }}
                >
                  <XAxis type="number" tick={{ fontSize: 13, fontWeight: 500 }} stroke={alpha(theme.palette.text.secondary, 0.3)} />
                  <YAxis type="category" dataKey={getXAxisKey()} width={70} tick={{ fontSize: 13, fontWeight: 500 }} stroke={alpha(theme.palette.text.secondary, 0.3)} />
                  <Tooltip
                    cursor={{ fill: alpha(theme.palette.primary.main, 0.05), radius: 8 }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload || payload.length === 0) return null;
                      const dataPoint = payload[0]?.payload;
                      return (
                        <Box sx={{ 
                          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                          borderRadius: 3, 
                          boxShadow: '0 8px 32px rgba(0,0,0,0.12)', 
                          p: 2.5, 
                          border: '2px solid', 
                          borderColor: alpha(theme.palette.primary.main, 0.2),
                          minWidth: 200
                        }}>
                          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5, color: theme.palette.text.primary, borderBottom: '2px solid', borderColor: alpha(theme.palette.primary.main, 0.2), pb: 1 }}>{label}</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.8 }}>
                            <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: theme.palette.primary.main }} />
                            <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 600, flex: 1 }}>Excl VAT:</Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 700 }} color="primary.main">€{dataPoint?.salesExclVAT || 0}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.8 }}>
                            <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: theme.palette.warning.main }} />
                            <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 600, flex: 1 }}>VAT:</Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 700 }} color="warning.main">€{dataPoint?.vat || 0}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 1, borderTop: '1px solid', borderColor: alpha(theme.palette.text.secondary, 0.1) }}>
                            <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: theme.palette.success.main }} />
                            <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 600, flex: 1 }}>Incl VAT:</Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 800 }} color="success.main">€{dataPoint?.salesInclVAT || 0}</Typography>
                          </Box>
                        </Box>
                      );
                    }}
                  />
                  <Legend 
                    verticalAlign="bottom" 
                    align="center" 
                    wrapperStyle={{ 
                      paddingTop: 12, 
                      fontSize: 13,
                      fontWeight: 600
                    }}
                    formatter={(value) => {
                      const labels = { salesExclVAT: t("total_sale_excl_vat") || "Excl VAT", vat: t("total_vat") || "VAT" };
                      return labels[value] || value;
                    }}
                    iconType="circle"
                  />
                  <Bar dataKey="salesExclVAT" stackId="sales" fill={theme.palette.primary.main} barSize={getBarSize(salesData)} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="vat" stackId="sales" fill={theme.palette.warning.main} barSize={getBarSize(salesData)} radius={[0, 4, 4, 0]}>
                    <LabelList content={renderSalesTotalLabel} />
                  </Bar>
                  <Bar dataKey="salesInclVAT" stackId="labelLayer" fill="none" barSize={0}>
                    <LabelList content={renderSalesTotalLabelEnd} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              {salesData.every(item => (item.salesExclVAT || 0) === 0 && (item.vat || 0) === 0) && (
                <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary", fontSize: 16, pointerEvents: "none" }}>
                  {t("noSales")}
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Invoice Status Donut */}
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={0} 
            sx={{ 
              borderRadius: 3, 
              height: '100%',
              border: '1px solid', 
              borderColor: alpha(theme.palette.info.main, 0.2), 
              background: `linear-gradient(145deg, ${alpha(theme.palette.info.main, 0.02)} 0%, white 50%, ${alpha(theme.palette.success.main, 0.03)} 100%)`,
              boxShadow: `0 4px 20px ${alpha(theme.palette.info.main, 0.08)}`,
              transition: 'all 0.3s ease',
              '&:hover': { 
                boxShadow: `0 8px 30px ${alpha(theme.palette.info.main, 0.15)}`,
                transform: 'translateY(-4px)'
              }
            }}
          >
            <Box sx={{ 
              px: { xs: 2, sm: 3 }, 
              py: { xs: 2.5, md: 1.25 }, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              flexWrap: 'wrap',
              gap: 1,
              borderBottom: '2px solid', 
              borderColor: alpha(theme.palette.info.main, 0.1), 
              background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)} 0%, ${alpha(theme.palette.success.main, 0.04)} 100%)`,
              borderRadius: '12px 12px 0 0'
            }}>
              <Typography variant="h6" fontWeight="700" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Receipt sx={{ fontSize: 24, color: theme.palette.info.main }} />
                {documentStatusLabel}
              </Typography>
            </Box>
            <Box sx={{ p: { xs: 1.5, sm: 2.5, md: 1.25 }, height: { xs: 380, sm: 340, md: 300 }, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              {invoiceStatusData.length === 0 || (invoiceStatusData.length === 1 && invoiceStatusData[0].name === "No Data") ? (
                <Typography variant="body1" color="text.secondary">{t("noInvoicesStatus")}</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', width: '100%', height: '100%', gap: 1 }}>
                  <Box sx={{ flex: 1, width: { xs: '100%', sm: 'auto' }, height: { xs: 220, sm: '100%' }, position: 'relative', minWidth: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie 
                          data={invoiceStatusData} 
                          cx="50%" 
                          cy="50%" 
                          outerRadius={90} 
                          innerRadius={60} 
                          paddingAngle={4} 
                          dataKey="value"
                          animationBegin={0}
                          animationDuration={800}
                        >
                          {invoiceStatusData.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={STATUS_COLORS[index % STATUS_COLORS.length]} 
                              stroke="white"
                              strokeWidth={2}
                            />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value, name) => [`${value}`, name]} 
                          contentStyle={{ 
                            borderRadius: 12, 
                            border: 'none', 
                            boxShadow: '0 8px 24px rgba(0,0,0,0.15)', 
                            fontSize: 14,
                            padding: '12px 16px',
                            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                          }} 
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <Box sx={{ 
                      position: 'absolute', 
                      top: '50%', 
                      left: '50%', 
                      transform: 'translate(-50%, -50%)', 
                      textAlign: 'center',
                      background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)} 0%, ${alpha(theme.palette.success.main, 0.08)} 100%)`,
                      borderRadius: '50%',
                      width: 110,
                      height: 110,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: `0 4px 16px ${alpha(theme.palette.info.main, 0.2)}`
                    }}>
                      <Typography variant="h4" fontWeight="900" color="text.primary">{totalInvoices}</Typography>
                      <Typography variant="caption" color="text.secondary" fontSize={12} fontWeight={600}>{documentLabel}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ minWidth: { xs: 0, sm: 140 }, maxWidth: { xs: '100%', sm: 180 }, width: { xs: '100%', sm: 'auto' }, height: { xs: 'auto', sm: '100%' }, display: 'flex', flexDirection: { xs: 'row', sm: 'column' }, flexWrap: { xs: 'wrap', sm: 'nowrap' }, justifyContent: 'center', overflow: 'auto', pr: 1 }}>
                    {invoiceStatusData.filter(s => s.name !== "No Data").map((status, i) => (
                      <Box 
                        key={status.name} 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1, 
                          mb: 1.2,
                          p: 1,
                          borderRadius: 2,
                          bgcolor: alpha(STATUS_COLORS[i % STATUS_COLORS.length], 0.05),
                          border: '1px solid',
                          borderColor: alpha(STATUS_COLORS[i % STATUS_COLORS.length], 0.15),
                          transition: 'all 0.2s',
                          flexShrink: 0,
                          '&:hover': {
                            bgcolor: alpha(STATUS_COLORS[i % STATUS_COLORS.length], 0.1),
                            transform: 'translateX(4px)'
                          }
                        }}
                      >
                        <Box sx={{ 
                          width: 10, 
                          height: 10, 
                          borderRadius: '50%', 
                          bgcolor: STATUS_COLORS[i % STATUS_COLORS.length],
                          boxShadow: `0 2px 8px ${alpha(STATUS_COLORS[i % STATUS_COLORS.length], 0.4)}`,
                          flexShrink: 0
                        }} />
                        <Typography variant="body2" color="text.secondary" fontSize={12} fontWeight={500} sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{status.name}</Typography>
                        <Typography variant="body2" fontWeight="800" fontSize={13} color={STATUS_COLORS[i % STATUS_COLORS.length]} sx={{ flexShrink: 0 }}>{status.value}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Row 2: Top Customers & Top Products Tables */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={0} 
            sx={{ 
              borderRadius: 3, 
              border: '1px solid', 
              borderColor: alpha(theme.palette.warning.main, 0.2), 
              background: `linear-gradient(145deg, ${alpha(theme.palette.warning.main, 0.02)} 0%, white 50%, ${alpha(theme.palette.warning.light, 0.03)} 100%)`,
              boxShadow: `0 4px 20px ${alpha(theme.palette.warning.main, 0.08)}`, 
              height: { xs: 360, sm: 340, md: 260 }, 
              display: 'flex', 
              flexDirection: 'column',
              transition: 'all 0.3s ease',
              '&:hover': { 
                boxShadow: `0 8px 30px ${alpha(theme.palette.warning.main, 0.15)}`,
                transform: 'translateY(-4px)'
              }
            }}
          >
            <Box sx={{ 
              px: { xs: 2, sm: 3 }, 
              py: { xs: 2.5, md: 1.25 }, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              flexWrap: 'wrap',
              gap: 1,
              borderBottom: '2px solid', 
              borderColor: alpha(theme.palette.warning.main, 0.1), 
              background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.08)} 0%, ${alpha(theme.palette.warning.light, 0.04)} 100%)`,
              borderRadius: '12px 12px 0 0'
            }}>
              <Typography variant="h6" fontWeight="700" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <People sx={{ fontSize: 24, color: theme.palette.warning.main }} />
                {t("topCustomers")}
              </Typography>
              <Button 
                size="small" 
                onClick={() => navigate('/customers')} 
                sx={{ 
                  textTransform: 'none', 
                  fontSize: '0.75rem', 
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  color: theme.palette.warning.dark,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.warning.main, 0.2)
                  }
                }}
              >
                {t("view_all") || "View all"}
              </Button>
            </Box>
            <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6, py: 2 }}>{t("customer") || "CUSTOMER"}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6, py: 2 }}>{t("amount") || "AMOUNT"}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topCustomersData.filter(c => c.name && c.amount > 0).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} align="center" sx={{ py: 5, color: 'text.secondary', fontSize: 14 }}>
                        {t("noTopCustomers") || "No data available"}
                      </TableCell>
                    </TableRow>
                  ) : (
                    topCustomersData.filter(c => c.name && c.amount > 0).map((customer, index) => (
                      <TableRow key={index} sx={{ '&:last-child td': { border: 0 }, '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.05), cursor: 'pointer' } }}>
                        <TableCell sx={{ py: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ width: 36, height: 36, fontSize: 13, fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                              {customer.name.substring(0, 2).toUpperCase()}
                            </Avatar>
                            <Typography variant="body2" fontWeight="600" fontSize="0.875rem">{customer.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="700" fontSize="0.9rem">€{customer.amount.toLocaleString()}</Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Top Products */}
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={0} 
            sx={{ 
              borderRadius: 3, 
              border: '1px solid', 
              borderColor: alpha(theme.palette.success.main, 0.2), 
              background: `linear-gradient(145deg, ${alpha(theme.palette.success.main, 0.02)} 0%, white 50%, ${alpha(theme.palette.success.light, 0.03)} 100%)`,
              boxShadow: `0 4px 20px ${alpha(theme.palette.success.main, 0.08)}`, 
              height: { xs: 360, sm: 340, md: 260 }, 
              display: 'flex', 
              flexDirection: 'column',
              transition: 'all 0.3s ease',
              '&:hover': { 
                boxShadow: `0 8px 30px ${alpha(theme.palette.success.main, 0.15)}`,
                transform: 'translateY(-4px)'
              }
            }}
          >
            <Box sx={{ 
              px: { xs: 2, sm: 3 }, 
              py: { xs: 2.5, md: 1.25 }, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              flexWrap: 'wrap',
              gap: 1,
              borderBottom: '2px solid', 
              borderColor: alpha(theme.palette.success.main, 0.1), 
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.success.light, 0.04)} 100%)`,
              borderRadius: '12px 12px 0 0'
            }}>
              <Typography variant="h6" fontWeight="700" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Inventory sx={{ fontSize: 24, color: theme.palette.success.main }} />
                {t("topProducts")}
              </Typography>
              <Button 
                size="small" 
                onClick={() => navigate('/products')} 
                sx={{ 
                  textTransform: 'none', 
                  fontSize: '0.75rem', 
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  color: theme.palette.success.dark,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.success.main, 0.2)
                  }
                }}
              >
                {t("view_all") || "View all"}
              </Button>
            </Box>
            <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6, py: 2 }}>{t("product") || "PRODUCT"}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6, py: 2 }}>{t("sold") || "SOLD"}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topSellingProductsData.filter(p => p.name && p.sold > 0).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} align="center" sx={{ py: 5, color: 'text.secondary', fontSize: 14 }}>
                        {t("noTopProducts") || "No data available"}
                      </TableCell>
                    </TableRow>
                  ) : (
                    topSellingProductsData.filter(p => p.name && p.sold > 0).map((product, index) => (
                      <TableRow key={index} sx={{ '&:last-child td': { border: 0 }, '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.05), cursor: 'pointer' } }}>
                        <TableCell sx={{ py: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ width: 36, height: 36, fontSize: 13, fontWeight: 700, bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }}>
                              {product.name.substring(0, 2).toUpperCase()}
                            </Avatar>
                            <Typography variant="body2" fontWeight="600" fontSize="0.875rem">{product.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Chip label={`${product.sold} ${t("sold") || "sold"}`} size="medium" sx={{ fontSize: 12, fontWeight: 600, bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.dark, height: 28 }} />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>


      
      {/* Add Customer Dialog */}
      <AddCustomerDialog
        open={customerDialogOpen}
        onClose={() => setCustomerDialogOpen(false)}
        onCustomerCreated={handleCustomerCreated}
        optionalFields={{ email: true, phone: true }}
      />
      
      {/* Add Product Dialog */}
      <AddProductDialog
        open={productDialogOpen}
        onClose={() => setProductDialogOpen(false)}
        onProductCreated={handleProductCreated}
      />
    </Box>
  );
};

export default Dashboard;